/**
 * A generated test report, rendered.
 *
 * `bodyKind: 'composed'` means the report is structured DATA, not a document —
 * so there is nothing to put in an iframe and every surface showing one has to
 * render it. That is why this lives in shared-ui rather than in either app: the
 * teacher opens a report from a student's attempt, the admin console opens the
 * same report from Operations, and a second copy of this would drift the moment
 * the report shape changed.
 *
 * `showLedger` is the difference between the two readers. A teacher reads the
 * prose; an operator reads the prose BESIDE the numbers it was written over,
 * because their question is whether the model stated something the figures do
 * not support.
 *
 * See docs/FeatureDesigns/student-performance-report-diagnosis/test-report-target.md.
 */

import { LEDGER_BUCKET_LABELS, type LedgerBucket } from '@padcanvas/shared';
import './TestReportBody.css';

interface QuestionNote { number?: number; questionId?: string; cause?: string; what?: string }
interface Recommendation { action?: string; evidence?: string; drill?: string; bucket?: string }

export interface TestReportBodyProps {
    /** The report's `body` — the model's prose over the ledger. */
    body: Record<string, unknown> | undefined;
    /** The report's `ledger`. Rendered only when `showLedger`. */
    ledger?: Record<string, unknown> | undefined;
    /** Show the figures beside the prose. Operators yes, teachers no. */
    showLedger?: boolean;
}

const bucketLabel = (b: string | undefined): string =>
    (b && LEDGER_BUCKET_LABELS[b as LedgerBucket]) || b || '';

const pct = (n: unknown): string =>
    (typeof n === 'number' ? `${Math.round(n * 100)}%` : '—');

/**
 * The numbers, read defensively.
 *
 * The ledger's shape lives in the backend and this is a view over whatever was
 * stored — including reports written before a field existed. A viewer that
 * threw on a missing key would hide the report it exists to show.
 */
function Ledger({ ledger }: { ledger: Record<string, unknown> }) {
    const buckets = (ledger.buckets ?? {}) as Record<string, number>;
    const counts = (ledger.bucketCounts ?? {}) as Record<string, number>;
    const strategy = (ledger.strategy ?? {}) as Record<string, unknown>;

    return (
        <aside className="trb-ledger">
            <h3 className="trb-h3">The ledger</h3>

            <div className="trb-stats">
                <div className="trb-stat">
                    <span className="trb-stat-value">
                        {String(ledger.attained ?? '—')} / {String(ledger.totalMarks ?? '—')}
                    </span>
                    <span className="trb-stat-label">Attained</span>
                </div>
                <div className="trb-stat">
                    <span className="trb-stat-value">{String(ledger.attainable ?? '—')}</span>
                    <span className="trb-stat-label">Attainable</span>
                </div>
                <div className="trb-stat">
                    <span className="trb-stat-value">{pct(strategy.accuracy)}</span>
                    <span className="trb-stat-label">Accuracy, of what was answered</span>
                </div>
                <div className="trb-stat">
                    <span className="trb-stat-value">{pct(strategy.attemptRate)}</span>
                    <span className="trb-stat-label">Attempt rate</span>
                </div>
                <div className="trb-stat">
                    <span className="trb-stat-value">{String(strategy.questionsLeftOnTable ?? '—')}</span>
                    <span className="trb-stat-label">Worked, never answered</span>
                </div>
            </div>

            {/* A ledger that does not reconcile is an error, not a rounding
                note — so it sits at the top of the numbers, not in a log. */}
            {ledger.reconciles === false && (
                <p className="trb-warn">
                    This ledger does not reconcile: attained plus the buckets do not equal the paper total.
                </p>
            )}

            <table className="trb-table">
                <thead><tr><th>Where the losses are</th><th>Marks</th><th>Questions</th></tr></thead>
                <tbody>
                    {(Object.keys(LEDGER_BUCKET_LABELS) as LedgerBucket[]).map(b => (
                        <tr key={b}>
                            <td>{LEDGER_BUCKET_LABELS[b]}</td>
                            <td className="trb-num">{buckets[b] ?? 0}</td>
                            <td className="trb-num">{counts[b] ?? 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </aside>
    );
}

export function TestReportBody({ body, ledger, showLedger }: TestReportBodyProps) {
    const b = body ?? {};
    const notes = (b.questionNotes ?? []) as QuestionNote[];
    const recs = (b.recommendations ?? []) as Recommendation[];

    if (!b.headline && !b.body && !recs.length) {
        return <p className="trb-empty">This report has no content to show.</p>;
    }

    return (
        <div className={showLedger && ledger ? 'trb trb--with-ledger' : 'trb'}>
            <div className="trb-main">
                {typeof b.headline === 'string' && <p className="trb-headline">{b.headline}</p>}

                {typeof b.body === 'string' && (
                    <div className="trb-prose">
                        {b.body.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
                    </div>
                )}

                {recs.length > 0 && (
                    <>
                        <h3 className="trb-h3">What to do next</h3>
                        <ol className="trb-recs">
                            {recs.map((r, i) => (
                                <li key={i}>
                                    <div className="trb-rec-action">{r.action}</div>
                                    {r.bucket && <div className="trb-rec-bucket">{bucketLabel(r.bucket)}</div>}
                                    {r.evidence && <p className="trb-rec-line"><strong>Why: </strong>{r.evidence}</p>}
                                    {r.drill && <p className="trb-rec-line"><strong>Try: </strong>{r.drill}</p>}
                                </li>
                            ))}
                        </ol>
                    </>
                )}

                {typeof b.prediction === 'string' && b.prediction && (
                    <>
                        <h3 className="trb-h3">The prediction</h3>
                        <p className="trb-prose">{b.prediction}</p>
                    </>
                )}

                {notes.length > 0 && (
                    <>
                        <h3 className="trb-h3">Question by question</h3>
                        <table className="trb-table">
                            <thead><tr><th>Q</th><th>Cause</th><th>What happened</th></tr></thead>
                            <tbody>
                                {notes.map((n, i) => (
                                    <tr key={i}>
                                        <td className="trb-num">{n.number ?? '—'}</td>
                                        <td>{n.cause}</td>
                                        <td>{n.what}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {/* Stated, never folded into the prose: a report that reads as
                    complete when it is partial is worse than none. */}
                {typeof b.limits === 'string' && b.limits && (
                    <>
                        <h3 className="trb-h3">What this report could not see</h3>
                        <p className="trb-prose">{b.limits}</p>
                    </>
                )}
            </div>

            {showLedger && ledger && <Ledger ledger={ledger} />}
        </div>
    );
}
